import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, LogIn, LogOut, FileText, Receipt, CheckCircle2, MessageSquare, Plane, Edit, Trash2, UserPlus, ShieldCheck, Upload, RefreshCcw } from 'lucide-react';
import api from '@/lib/api';

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  claim_submitted: Receipt,
  claim_decided: CheckCircle2,
  leave_decided: Plane,
  payslip_acknowledged: ShieldCheck,
  payslip_disputed: MessageSquare,
  hr_doc_uploaded: Upload,
  hr_doc_renewed: RefreshCcw,
  user_create: UserPlus,
  user_update: Edit,
  user_delete: Trash2,
  create: FileText,
  update: Edit,
  delete: Trash2,
};

const ACTION_COLORS = {
  login: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
  logout: 'text-stone-500 bg-stone-100 dark:bg-stone-800',
  claim_submitted: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
  claim_decided: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
  leave_decided: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
  payslip_acknowledged: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
  payslip_disputed: 'text-rose-600 bg-rose-100 dark:bg-rose-900/30',
  hr_doc_uploaded: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
  hr_doc_renewed: 'text-violet-600 bg-violet-100 dark:bg-violet-900/30',
  user_create: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
  user_update: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
  user_delete: 'text-rose-600 bg-rose-100 dark:bg-rose-900/30',
};

const friendly = (action) => (action || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function ActivityFeedPanel({ scope = 'me', userId = null, limit = 50, title = 'Activity' }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = scope === 'user' && userId ? `/users/${userId}/activity` : '/me/activity';
        const r = await api.get(`${path}?limit=${limit}`);
        if (!cancelled) {
          setItems(r.data?.items || []);
          setCounts(r.data?.action_counts || {});
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scope, userId, limit]);

  return (
    <Card data-testid="activity-feed-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <Activity size={14} className="text-violet-500" /> {title}
        </CardTitle>
        <span className="text-xs text-muted-foreground font-mono">{items.length} entries</span>
      </CardHeader>
      <CardContent className="p-0">
        {/* Action mix bar */}
        {Object.keys(counts).length > 0 && (
          <div className="px-4 py-2 border-b border-border flex flex-wrap gap-1">
            {Object.entries(counts).slice(0, 8).map(([action, count]) => (
              <span key={action} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ACTION_COLORS[action] || 'bg-muted text-muted-foreground'}`}>
                {friendly(action)} · {count}
              </span>
            ))}
          </div>
        )}

        <div className="divide-y divide-border max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">No activity recorded yet.</div>
          ) : items.map((it, i) => {
            const action = it.action || '';
            const Icon = ACTION_ICONS[action] || ACTION_ICONS[action.split('_')[0]] || Activity;
            const colorClass = ACTION_COLORS[action] || 'bg-muted text-muted-foreground';
            const ts = (it.timestamp || it.created_at || '').replace('T', ' ').slice(0, 19);
            return (
              <div key={it.id || i} className="px-4 py-2.5 flex items-start gap-3 hover:bg-muted/30" data-testid={`activity-row-${i}`}>
                <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${colorClass}`}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground capitalize">
                    {friendly(action)}
                    {it.resource && <span className="text-muted-foreground font-normal text-xs ml-1.5">· {it.resource}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                    {ts}
                    {it.user_email && scope !== 'me' && <span className="ml-2">· {it.user_email}</span>}
                    {it.ip_address && <span className="ml-2">· {it.ip_address}</span>}
                  </div>
                  {it.details && Object.keys(it.details).length > 0 && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate">
                      {Object.entries(it.details).slice(0, 4).map(([k, v]) => `${k}:${v}`).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
